import { getSessionGeneration } from '../../lib/sessionGeneration.js'
import { rosterSnapshot, rosterFailure, rosterPending, rosterPendingText, subscribeRosterPending, matchingRosterReceipt, acknowledgeRosterReceipt } from '../../lib/rosterWrite.js'
import { useCallback, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { api, onSessionChange } from '../../lib/api.js'
import { useAccess } from '../../lib/access.jsx'
import { ACTIONS } from '../../lib/rbac.js'
import { bodyKeysFor, wouldDecide, LANE_LEADS, QUALIFIED_FIELD } from './bodyRoster.js'
import styles from './BodyRoster.module.css'

/**
 * ADD A REP TO A GOVERNING BODY — from the body's own card.
 *
 * Brian, 2026-10-14, working the Governing Body view card by card:
 *   "in governing body - i need to be able to add a rep to a governing body."
 *   "dalton should be assigned UIL but not the entire state."
 *   "uil will have plenty of personnel that are working on it."
 *
 * ⚠️ IT WRITES THE MAP, NOT THE RECORDS, AND IT SAYS SO ON ITS FACE.
 *
 * The obvious build here is a rep dropdown that stamps `sr_assigned_rep` onto
 * the card's records. That is the wrong build, and it is wrong in a way that
 * gets worse over time: a hand-set owner the assignment map cannot explain
 * comes back as a CONFLICT on every future apply-map run, forever. So this
 * posts a RULE — this body is worked by these people, led by this one — and
 * the server derives the record writes separately, under a guard that reports
 * an already-owned record rather than overwriting it.
 *
 * The consequence is that pressing this button changes NO record, and the
 * panel has to be honest about that rather than flashing a tick. A control
 * that reports success while every record still names the old rep is a control
 * that lies by omission.
 *
 * ⚠️ THE KEY IS THE QUALIFIED BODY STRING, AND IT IS SHOWN, NOT ASSUMED.
 * A card is a registry entity; the assignment is keyed on what the records
 * carry. Measured 2026-10-14: the Texas card's records carry SIXTEEN different
 * UIL body strings, and the California card's carry eleven CIF ones. "Assign
 * this card" is not a well-formed instruction, so this lists the real keys
 * with their record counts and current holders and makes the person pick one.
 * That is also what stops a bare "ACAA" — two different associations, two
 * different reps — ever reaching the server.
 *
 * ⚠️ MOBILE FIRST. Brian works this on a phone. One column, full-width
 * controls, 44px touch targets, no horizontal scroll at 380px.
 *
 * NO MONEY ANYWHERE IN THIS PANEL. Record counts only; money is manager-only
 * and this is a territory control, not a revenue one.
 */

const nf = (n) => (typeof n === 'number' ? n.toLocaleString() : '—')

/* Named exports so check-shipped-features.mjs can assert the SENTENCE that
 * ships rather than a fragment the JSX transform happened to leave intact —
 * the same reason GoverningBodies.jsx exports its caveats. */
export const ROSTER_WRITES_THE_MAP =
  'This sets who works the body in the assignment map. It does not change any record by itself — the '
  + 'map is what new records inherit, and existing records are moved by a separate, audited apply step '
  + 'that reports anything a person already assigned instead of overwriting it.'

/* ⚠️ THE WORDS HERE ARE THE HOUSE VOCABULARY, NOT A PARAPHRASE OF IT.
 * `check-ownership-language.mjs` refuses "owner", "territory" and "book" in
 * anything a person reads, because a rep is ASSIGNED a record and HOLDS that
 * assignment for a period — and reps read labels as the arrangement. This
 * panel is the surface where that distinction matters most, so it says
 * "assigned rep" and "accountable for" throughout. The engineering comments in
 * this file are out of the gate's scope and use the plainer word; the copy
 * does not. */
export const ROSTER_LEAD_RULE =
  'Several reps can work one body. Every record still gets exactly ONE assigned rep who is '
  + 'accountable for it, so a body with more than one rep must name the LEAD — that is the rep a '
  + 'record inherits. Coverage is many-to-one; accountability is one-to-one.'

export const ROSTER_QUALIFIED_RULE =
  'Assignment is keyed on the exact governing-body string the records carry, never on the acronym. '
  + 'ACAA is both the Alabama Christian Athletic Association and the Atlantic Collegiate Athletic '
  + 'Association, and they are held by different reps.'

export default function BodyRoster({ entity, rows = [], initiallyOpen = false }) {
  const instanceId = useId()
  const formId = `${entity?.key || 'body'}-${instanceId}`
  const { can } = useAccess()
  const allowed = can(ACTIONS.CC_REP_ASSIGN)

  const [map, setMap] = useState(null)
  const [mapErr, setMapErr] = useState('')
  const [open, setOpen] = useState(initiallyOpen)
  const [sel, setSel] = useState('')
  const [reps, setReps] = useState([])
  const [lead, setLead] = useState('')
  const [reason, setReason] = useState('')
  const [who, setWho] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [audit, setAudit] = useState(null)
  const [auditError, setAuditError] = useState('')
  const readSequence = useRef(0)
  const writing = useRef(false)
  const mapGeneration = useRef(null)
  useSyncExternalStore(subscribeRosterPending, rosterPendingText, () => '')
  const pending = rosterPending()
  const receipt = matchingRosterReceipt(pending, audit)
  useEffect(() => onSessionChange(() => {
    readSequence.current++; mapGeneration.current = null
    setMap(null); setMapErr(''); setAudit(null); setLoading(false); setResult(null); setError(null); setReason(''); setWho('')
  }), [])
  useEffect(() => () => { readSequence.current++ }, [])

  /* ⚠️ THE MAP IS ONLY FETCHED WHEN THE PANEL IS OPENED. It is one request per
   * card otherwise, on a page that renders sixty of them. */
  const loadMap = useCallback(async () => {
    const sequence = ++readSequence.current, generation = getSessionGeneration()
    setLoading(true); setMap(null); setMapErr(''); setAudit(null); setAuditError('')
    try {
      const d = await api.assignmentMap()
      rosterSnapshot(d)
      if (sequence !== readSequence.current || generation !== getSessionGeneration()) return
      mapGeneration.current = generation; setMap(d)
      const unresolved = rosterPending()
      if (unresolved?.body) {
        try {
          const entries = await api.auditRead({ recordId: 'body:' + unresolved.body, field: 'assignment_body_roster', limit: 1000 })
          if (sequence === readSequence.current && generation === getSessionGeneration()) setAudit(entries)
        } catch {
          if (sequence === readSequence.current) setAuditError('The assignment audit record could not be read. The previous outcome remains unresolved.')
        }
      }
    } catch (e) {
      if (sequence === readSequence.current && generation === getSessionGeneration()) {
        setMap(null); setMapErr(e?.message || 'Could not read the assignment map.')
      }
    } finally { if (sequence === readSequence.current) setLoading(false) }
  }, [])
  useEffect(() => { if (open && allowed && !map && !mapErr && !loading) loadMap() }, [open, allowed, map, mapErr, loading, loadMap])

  const { keys, unofferable, qualifiedRecords } = useMemo(() => bodyKeysFor(rows), [rows])
  const chosen = useMemo(() => keys.find((k) => k.body === sel) || null, [keys, sel])
  const mapped = useMemo(
    () => (map?.bodies || []).find((b) => b.body === sel) || null,
    [map, sel],
  )
  const impact = useMemo(() => (chosen ? wouldDecide(chosen, LANE_LEADS) : null), [chosen])

  const REPS = map?.reps || {}
  const repNames = useMemo(() => Object.keys(REPS).sort(), [REPS])

  /* Pre-fill from whatever the body already has, so an edit starts from the
   * truth rather than from blank — and so pressing save without touching
   * anything is a no-op rather than a wipe. */
  useEffect(() => {
    if (!mapped) { setReps([]); setLead(''); return }
    setReps(mapped.reps || [])
    setLead(mapped.lead || mapped.resolved?.lead || '')
  }, [mapped])

  const toggleRep = (r) => {
    setReps((cur) => {
      const next = cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]
      /* If the lead just came off the roster, drop it — a lead who is not on
       * the body is how a record gets an owner nobody put there, and the
       * server refuses it anyway. Better to show that here than to 400. */
      if (lead && !next.includes(lead)) setLead(next.length === 1 ? next[0] : '')
      else if (next.length === 1) setLead(next[0])
      return next
    })
  }

  const needsLead = reps.length > 1 && !lead
  const canSave = !!sel && reps.length > 0 && !needsLead && reason.trim().length > 0 && who.trim().length > 0 && !busy && !loading && !!map && !mapErr && !pending

  const save = async () => {
    if (!allowed || !canSave || writing.current || mapGeneration.current !== getSessionGeneration()) return
    writing.current = true
    const submitGeneration = getSessionGeneration()
    const reviewed = rosterSnapshot(map)
    setBusy(true); setError(null); setResult(null)
    try {
      const d = await api.assignmentMapEntry({
        body: sel,
        expectedFingerprint: reviewed.fingerprint,
        reps,
        lead: lead || (reps.length === 1 ? reps[0] : ''),
        reason: reason.trim(),
        authorizedBy: who.trim(),
      })
      if (submitGeneration !== getSessionGeneration()) return
      setResult(d)
      await loadMap()
    } catch (e) {
      if (submitGeneration !== getSessionGeneration()) return
      const failure = rosterFailure(e)
      setError({ ...failure, bodies: e?.body?.bodies || null, valid: e?.body?.valid || null })
      if (failure.kind !== 'refused') {
        readSequence.current++; setLoading(false); setMap(null); setAudit(null)
        setMapErr('Reload and review the assignment map before making another change.')
      }
    } finally { writing.current = false; setBusy(false) }
  }

  /* ⚠️ HIDDEN RATHER THAN SHOWN-AND-BROKEN for a rep without the grant. This is
   * navigation hygiene, NOT access control — the server refuses the write to
   * everyone without `cc_rep_assign`, including every service identity, and
   * that refusal is the enforcement. */
  if (!allowed) return null

  return (
    <div className={styles.wrap}>
      <button type="button" className={styles.toggle} aria-expanded={open}
        onClick={() => setOpen((o) => !o)}>
        {open ? 'Close rep assignment' : 'Assign a rep to this body ›'}
      </button>

      {open && (
        <div className={styles.panel}>
          <p className={styles.rule}>{ROSTER_WRITES_THE_MAP}</p>
          {map && <p className={styles.hint}>{map.rosterSnapshot.present
            ? Object.keys(map.rosterSnapshot.document.bodies).length ? 'Authoritative assignment roster is present.' : 'Authoritative assignment roster is present with no overrides.'
            : 'Authoritative assignment roster has not been set up. Sheet suggestions are provisional; the first saved assignment changes how Bowl setup is evaluated.'}</p>}
          {(mapErr || pending) && <div className={styles.err} role="status">
            {pending ? <p><b>Assignment outcome needs reconciliation.</b> {pending.body || 'Browser storage is unavailable.'} A prior change may have been saved. Do not repeat it.</p> : <p>{mapErr}</p>}
            {auditError && <p>{auditError}</p>}
            <button type="button" className={styles.save} disabled={busy || loading} onClick={loadMap}>
              {loading ? 'Reading assignment map.' : 'Reload map for review'}
            </button>
            {pending && map && audit && !receipt && <p>No matching durable receipt was found in the returned audit history. This does not prove that nothing changed. Ask an executive to reconcile the pending change; saving stays blocked.</p>}
            {receipt && <><p>A matching change is recorded in the permanent ledger at {receipt.at}. Review the current map before starting a new proposal.</p>
              <button type="button" className={styles.save} disabled={busy || loading} onClick={() => {
                acknowledgeRosterReceipt(pending.id, map, audit); setError(null); setMapErr(''); setReason(''); setWho('')
              }}>I reviewed the map and recorded change</button></>}
          </div>}

          {/* ---- 1. WHICH BODY. Never implied from the card. ---- */}
          <label className={styles.label} htmlFor={`body-${formId}`}>
            Which governing body
          </label>
          <p className={styles.hint}>{ROSTER_QUALIFIED_RULE}</p>
          {keys.length === 0 ? (
            /* ⚠️ A SILENT ZERO IS THE ONE THING THIS MUST NOT DO. An empty
             * select and a broken control look identical, so it says which. */
            <p className={styles.gap}>
              {rows.length === 0
                ? 'This card holds no records, so there is no governing-body string on it to assign. '
                  + 'Read the zero on the card above for which kind of zero that is.'
                : `This card holds ${nf(rows.length)} records and not one of them carries a governing-body `
                  + 'string that can be assigned. That is a data repair on governing_body, not an assignment.'}
              {unofferable.length > 0 && (
                <span className={styles.gapWhy}>
                  {' '}Excluded: {unofferable.map((u) => `${u.why} (${nf(u.count)})`).join(' · ')}
                </span>
              )}
            </p>
          ) : (
            <select id={`body-${formId}`} className={styles.select}
              value={sel} onChange={(e) => { setSel(e.target.value); setResult(null) }}>
              <option value="">Choose the body to assign…</option>
              {keys.map((k) => (
                <option key={`${k.field}::${k.body}`} value={k.body}>
                  {k.body} — {nf(k.records)} {k.records === 1 ? 'record' : 'records'}
                  {k.field === QUALIFIED_FIELD ? '' : ' (full name)'}
                </option>
              ))}
            </select>
          )}

          {/* ⚠️ THE CARD'S KEYS MUST ADD UP TO THE CARD'S RECORDS, and it says
              so when they do not — otherwise a key is missing from the list
              above and this control silently does not cover some records. */}
          {keys.length > 0 && qualifiedRecords !== rows.length && (
            <p className={styles.warn}>
              This card holds {nf(rows.length)} records but its qualified governing-body keys account for{' '}
              {nf(qualifiedRecords)}. The difference carries no qualified body string, so assigning every
              key above would still leave {nf(Math.max(0, rows.length - qualifiedRecords))} unreachable
              from this control.
            </p>
          )}

          {chosen && (
            <>
              {/* ---- WHAT IS TRUE TODAY, before anything is changed ---- */}
              <div className={styles.now}>
                <p className={styles.nowHead}>
                  <span className={styles.qualified}>{chosen.body}</span>
                  <span className={styles.field}>{chosen.field}</span>
                </p>
                <p className={styles.nowLine}>
                  <b>On the records today:</b>{' '}
                  {chosen.reps.length === 0
                    ? <span data-gap="1">nobody — all {nf(chosen.records)} unassigned</span>
                    : chosen.reps.map((r) => `${r.rep} ${nf(r.count)}`).join(' · ')}
                  {chosen.unassigned > 0 && chosen.reps.length > 0 && (
                    <span data-gap="1"> · {nf(chosen.unassigned)} unassigned</span>
                  )}
                </p>
                <p className={styles.nowLine}>
                  <b>In the assignment map:</b>{' '}
                  {mapErr
                    ? <span data-gap="1">could not be read — {mapErr}. Not setting anything until it can be.</span>
                    : !map
                      ? 'reading…'
                      : mapped
                        ? <>
                            {(mapped.reps || []).join(' + ') || 'nobody'}
                            {mapped.resolved?.lead ? ` — lead ${mapped.resolved.leadName || mapped.resolved.lead}` : ''}
                            {' '}<span className={styles.src}>({mapped.source})</span>
                            {mapped.resolved?.needsLead && (
                              <span className={styles.warnInline}>
                                {' '}⚠ several reps and NO lead, so every record on this body is skipped as
                                “no lead declared”. Naming the lead below fixes that.
                              </span>
                            )}
                          </>
                        : <span data-gap="1">
                            nothing — this body is not in the map, so new records on it inherit no rep and
                            apply-map skips it as “no rep on the breakdown for this body”.
                          </span>}
                </p>
                {impact?.likelyLaneHeld > 0 && (
                  <p className={styles.nowLine}><b>Note:</b> {impact.note}</p>
                )}
              </div>

              {/* ---- 2. WHO WORKS IT. N reps, deliberately. ---- */}
              <p className={styles.label}>Who works this body</p>
              <p className={styles.hint}>{ROSTER_LEAD_RULE}</p>
              {!map && !mapErr && <p className={styles.hint}>Loading the rep roster…</p>}
              <div className={styles.reps}>
                {repNames.map((r) => (
                  <button type="button" key={r} className={styles.rep}
                    data-on={reps.includes(r) ? '1' : '0'}
                    onClick={() => toggleRep(r)}>
                    {REPS[r] || r}
                  </button>
                ))}
              </div>

              {/* ---- 3. THE ONE ACCOUNTABLE OWNER ---- */}
              {reps.length > 1 && (
                <>
                  <label className={styles.label} htmlFor={`lead-${formId}`}>
                    Lead — the one rep a record on this body inherits
                  </label>
                  <select id={`lead-${formId}`} className={styles.select}
                    value={lead} onChange={(e) => setLead(e.target.value)}>
                    <option value="">Choose the lead…</option>
                    {reps.map((r) => <option key={r} value={r}>{REPS[r] || r}</option>)}
                  </select>
                  {needsLead && (
                    <p className={styles.warn}>
                      {reps.length} reps and no lead. Every record on this body would be skipped as “no lead
                      declared” — a visible gap, but still a gap. Name the lead.
                    </p>
                  )}
                </>
              )}

              {/* ---- 4. WHY, AND WHO DECIDED ---- */}
              <label className={styles.label} htmlFor={`reason-${formId}`}>
                Why (written to the permanent ledger)
              </label>
              <input id={`reason-${formId}`} className={styles.input} value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. follows the 2026-2027 breakdown: Chris already holds AHSAA and AISA" />
              <label className={styles.label} htmlFor={`who-${formId}`}>
                Authorised by — the person who decided
              </label>
              <input id={`who-${formId}`} className={styles.input} value={who}
                onChange={(e) => setWho(e.target.value)} placeholder="e.g. Brian Kass" />
              <p className={styles.hint}>
                Reassigning a rep is a human decision, so the ledger records who decided and not just what
                ran. This is never filled in for you.
              </p>

              <button type="button" className={styles.save} disabled={!canSave} onClick={save}>
                {busy ? 'Writing the map…' : 'Set who works this body'}
              </button>

              {/* ---- 5. WHAT ACTUALLY HAPPENED ---- */}
              {error && (
                <div className={styles.err}>
                  <p><b>{error.title}.</b> {error.detail}</p>
                  {error.bodies && (
                    <ul>{error.bodies.map((b) => <li key={b}>{b}</li>)}</ul>
                  )}
                  {error.valid && <p>Valid reps: {error.valid.join(', ')}</p>}
                </div>
              )}
              {result && (
                <div className={styles.ok}>
                  <p>
                    <b>The map now says {result.entry?.reps?.join(' + ')}</b>
                    {result.resolved?.leadName ? `, lead ${result.resolved.leadName}` : ''} on{' '}
                    <span className={styles.qualified}>{result.body}</span>.
                  </p>
                  {result.previous && (
                    <p>
                      Replaced {result.previous.reps?.join(' + ')}
                      {result.previous.lead ? ` (lead ${result.previous.lead})` : ''}, set by{' '}
                      {result.previous.authorizedBy || 'unknown'}. The previous roster is recorded in the
                      ledger, so a rep who lost records from their assignment can have them reconstructed.
                    </p>
                  )}
                  {/* ⚠️ THE HONEST BIT. It would be easy to stop at the tick. */}
                  <p className={styles.zero}>
                    <b>{nf(result.recordsTouched ?? 0)} records changed assigned rep.</b> {result.next}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
